import React, { lazy, Suspense, useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { baseUrl } from '@fs/zion-config'
import getPermission from '@fs/zion-permissions'
import { useQueryParams } from '@fs/zion-router'
import {
  Button,
  colors,
  Divider,
  IconButton,
  Image,
  LayoutBand,
  Paragraph,
  Row,
  useContainerWidth,
  useOverlay,
} from '@fs/zion-ui'
import { MenuClose, NoticeQuestion, PlaceWorld } from '@fs/zion-icon'
import LangPicker from '@fs/zion-lang-picker'
import { useUser } from '@fs/zion-user'
import { getFullName, getGivenNames } from '../../../lib/helpers'
import { useCache, useNavigate } from '../../../hooks'
import { getFsAccountActivationStatus, getMemberData, getTreeData } from '../../../lib/services'
import { lcrCacheMemberKey, tryDemoSessionCache } from '../../../lib/tryDemoServices'
import { memberDataMunge } from '../../../lib/mungers'
import BoldParagraph from '../BoldParagraph'
import logoSansText from '../../../images/fs-logo-sans-text.svg'
import logo from '../../../images/fs-logo.svg'

const AddCredentialsOverlay = lazy(() => import('../overlays/AddCredentialsOverlay'))

const logoHeight = 62 // White Header with FS logo

/**
 * @describe - Display the Custom FNA Header.
 */
export default function FnaHeader() {
  const [t, i18n] = useTranslation()
  const { signedIn } = useUser()
  const { query } = useQueryParams()
  const { memberUuid, hideExtraButtons } = query
  const { isTryDemo, location, onPush } = useNavigate()
  const [isDismissed, setIsDismissed] = useState(false)
  const [isAccountActivated, setIsAccountActivated] = useState(null)
  const [membershipInfo, setMembershipInfo] = useState(null)
  const [tryDemoCachedMember, setTryDemoCachedMember] = useState()
  const [, setCachedSeenAccInstructionsValue] = useCache(`hasSeenAccountInstructions:${memberUuid}`)
  const isScreenshotsSession = hideExtraButtons === 'true'
  const showUnclaimedAccount =
    membershipInfo &&
    isAccountActivated === false &&
    !isScreenshotsSession &&
    !!memberUuid &&
    !location.pathname.includes('/account') &&
    !location.pathname.includes('/verify-account') &&
    !isDismissed
  const helpeeFullName = isTryDemo
    ? t('header.helpee-default-name', 'John Smith')
    : (tryDemoCachedMember?.name ?? getFullName(membershipInfo))
  const helpeeFirstNames = getGivenNames(membershipInfo)
  const langPickerOverlay = useOverlay()
  const addCredentialsOverlay = useOverlay()
  const atSize = useContainerWidth()
  const isMobile = atSize({ default: true, xl: false })
  const contactUsPath = `${baseUrl}/${i18n.language}/fieldops/familysearch-support-contact-us`
  const showHelpeeText =
    !location.pathname.includes('/helper-preview') &&
    !location.pathname.includes('/leader-preview') &&
    !location.pathname.includes('/verify-account') &&
    !location.pathname.includes('/qr-scan') &&
    query.fromMember !== 'true' &&
    membershipInfo

  const handleContactUs = useCallback(
    () => onPush(contactUsPath, 'Header:Contact Us:Click', true, true),
    [contactUsPath, onPush]
  )

  const handleDismiss = useCallback(() => setIsDismissed(true), [])

  const handleSeeInstructionsClick = useCallback(
    (event) => {
      addCredentialsOverlay.handleClick(event)
      setCachedSeenAccInstructionsValue(true) // Set the cache to indicate that the user has seen the instructions
    },
    [addCredentialsOverlay, setCachedSeenAccInstructionsValue]
  )

  useEffect(() => {
    if (isAccountActivated === null && signedIn) {
      getFsAccountActivationStatus(memberUuid)
        .then((accountStatus) => {
          const hasAccount = !!accountStatus.cisId
          if (hasAccount) {
            getPermission('FtMemberUiPermission').then(({ authorized }) => {
              if (authorized)
                getTreeData(memberUuid, '').then(() => {
                  setIsAccountActivated(!!accountStatus.fullyRegistered)
                })
              else setIsAccountActivated(false)
            })
          } else {
            setIsAccountActivated(false)
          }
        })
        .catch(() => {
          setIsAccountActivated(false)
        })
    }
  }, [setIsAccountActivated, memberUuid, isAccountActivated, signedIn])

  useEffect(() => {
    if (signedIn) {
      getMemberData(memberUuid).then((memberResponse) => {
        setMembershipInfo(memberDataMunge(memberResponse?.data))
      })
    }
  }, [memberUuid, signedIn])

  // check for member saved during lcr demo
  useEffect(() => {
    const getTryDemoMember = async () => {
      const cacheMember = await tryDemoSessionCache.getItem(lcrCacheMemberKey)
      setTryDemoCachedMember(cacheMember)
    }
    signedIn && isTryDemo && getTryDemoMember()
  }, [isTryDemo, memberUuid, signedIn])

  // #region Translations (excluding common-ui namespace strings)
  const youAreHelping = (
    <Trans
      i18nKey="header.you-are-helping"
      values={{ fullName: helpeeFullName }}
      defaults="You are helping <strong>{fullName}</strong>"
    />
  )
  const contactUs = t('header.action.contact-us', 'Contact Us')
  const selectLanguage = t('header.action.select-a-language', 'Select a language')

  // SubHeader
  const userNeedsCredentials = t(
    'header.member-needs-credentials',
    '{firstNames} needs to add a username and password',
    { firstNames: helpeeFirstNames }
  )
  const seeInstructionsButton = t('header.action.see-instructions', 'See Instructions')

  // if (!membershipInfo) return null

  // #endregion
  return (
    <>
      <LayoutBand
        color={colors.gray00}
        minHeight={logoHeight}
        marginX={atSize({ default: 'xxs', xs: 'xs', sm: 'sm', md: 'md', lg: 'lg' })}
        maxWidth="xxl"
      >
        <Row alignY="middle" guttersY="none">
          {isMobile ? (
            <Image alignY="middle" src={logoSansText} alt="FamilySearch" width={34} height={34} noCrop />
          ) : (
            <Image alignY="middle" src={logo} alt="FamilySearch" width={150} height={34} noCrop />
          )}
          {showHelpeeText ? (
            <Paragraph alignY="middle" centered data-testid="helpee-header" growX size="sm">
              {youAreHelping}
            </Paragraph>
          ) : (
            <div alignY="middle" growX />
          )}
          <div alignY="middle" style={{ width: isMobile ? 0 : '150px', height: logoHeight }} />

          <IconButton
            data-testid="header-pick-a-language-button"
            color="gray"
            Icon={PlaceWorld}
            onClick={langPickerOverlay.handleClick}
            title={selectLanguage}
          />

          {isMobile ? (
            <IconButton
              alignY="middle"
              color="gray"
              data-testid="header-contact-us-button"
              Icon={NoticeQuestion}
              onClick={handleContactUs}
              title={contactUs}
            />
          ) : (
            <Button
              alignY="middle"
              color="gray"
              data-testid="header-contact-us-button"
              emphasis="low"
              Icon={NoticeQuestion}
              onClick={handleContactUs}
            >
              {contactUs}
            </Button>
          )}
        </Row>
      </LayoutBand>

      {/* SubHeader -- For leaders helpers proxying for a member that has not claimed their account yet */}
      {showUnclaimedAccount && (
        <LayoutBand color={colors.blue02} maxWidth="xxl" marginY="nano">
          <Row>
            <div growX>
              <Row alignX="center" alignY="middle" guttersY="nano" wrap>
                <BoldParagraph size="xs">{userNeedsCredentials}</BoldParagraph>
                <Button data-testid="see-instructions-button" emphasis="medium" onClick={handleSeeInstructionsClick}>
                  {seeInstructionsButton}
                </Button>
              </Row>
            </div>
            <IconButton
              data-testid="dismiss-button"
              alignY="middle"
              Icon={MenuClose}
              noShrinkX
              onClick={handleDismiss}
            />
          </Row>
        </LayoutBand>
      )}
      <Divider />

      <Suspense fallback="">{langPickerOverlay.isOpen && <LangPicker overlay={langPickerOverlay} />}</Suspense>

      <Suspense fallback="">
        {addCredentialsOverlay.isOpen && (
          <AddCredentialsOverlay hideSkip membershipInfo={membershipInfo} overlay={addCredentialsOverlay} />
        )}
      </Suspense>
    </>
  )
}
